import { useState, useEffect } from "react";
import { Link } from "react-router";
import { ArrowLeft } from "lucide-react";
import { fetchLegalPages } from "./supabase";
import { useTheme } from "./ThemeInjector";
import { useLanguage } from "./LanguageContext";

const POLICY_ORDER = ["privacy-policy", "terms", "shipping-returns", "cookie-policy", "legal-info"];

function simpleMarkdownToHtml(text: string): string {
  return text
    .replace(/\n\n+/g, "</p><p>")
    .replace(/\n/g, "<br/>")
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
    .replace(/\[([^\]]+)\]\((mailto:[^)]+)\)/g, '<a href="$2">$1</a>')
    .replace(/^### (.+)$/gm, "<h3>$1</h3>")
    .replace(/^## (.+)$/gm, "<h2>$1</h2>")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/^(.+)$/gm, (line) => (line.startsWith("<") ? line : "<p>" + line + "</p>"));
}

type LegalEntry = { slug: string; title: string; content: string };

export function PoliciesPage() {
  const theme = useTheme();
  const { t } = useLanguage();
  const [pages, setPages] = useState<LegalEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    fetchLegalPages().then(({ data, error }) => {
      setLoading(false);
      if (error || !data || !Array.isArray(data)) return;
      const bySlug = new Map<string, LegalEntry>();
      data.forEach((row: { slug: string; title: string; content?: string }) => {
        bySlug.set(row.slug, {
          slug: row.slug,
          title: row.title ?? row.slug,
          content: row.content ?? "",
        });
      });
      const ordered = POLICY_ORDER.map((slug) => bySlug.get(slug)).filter(Boolean) as LegalEntry[];
      const rest = data
        .filter((row: { slug: string }) => !POLICY_ORDER.includes(row.slug))
        .map((row: { slug: string; title: string; content?: string }) => ({
          slug: row.slug,
          title: row.title ?? row.slug,
          content: row.content ?? "",
        }));
      setPages([...ordered, ...rest]);
    });
  }, []);

  if (loading) {
    return (
      <div className="pt-28 pb-24 px-6 max-w-3xl mx-auto text-center">
        <p style={{ fontFamily: theme.fontBody, fontSize: "15px", color: theme.muted }}>Loading…</p>
      </div>
    );
  }

  const current = pages[activeTab];

  return (
    <div className="pt-28 pb-24 px-6">
      <div className="max-w-4xl mx-auto">
        <Link
          to="/"
          className="inline-flex items-center gap-2 mb-10 tracking-[0.15em] uppercase transition-opacity hover:opacity-80"
          style={{ fontFamily: theme.fontBody, fontSize: "11px", color: theme.muted }}
        >
          <ArrowLeft size={14} /> Home
        </Link>
        <h1
          className="tracking-[0.06em] mb-8"
          style={{ fontFamily: theme.fontHeading, fontSize: "clamp(24px, 3vw, 32px)", fontWeight: 400, color: theme.fg }}
        >
          {t("nav.policies")}
        </h1>

        {/* Tabs */}
        {pages.length > 0 && (
          <div
            className="flex flex-wrap gap-2 mb-10 border-b"
            style={{ borderColor: theme.border }}
            role="tablist"
            aria-label="Policy sections"
          >
            {pages.map((p, i) => (
              <button
                key={p.slug}
                type="button"
                role="tab"
                aria-selected={activeTab === i}
                aria-controls={`policy-panel-${p.slug}`}
                id={`tab-${p.slug}`}
                onClick={() => setActiveTab(i)}
                className="px-4 py-3 tracking-[0.1em] uppercase transition-colors"
                style={{
                  fontFamily: theme.fontBody,
                  fontSize: "11px",
                  fontWeight: activeTab === i ? 500 : 300,
                  color: activeTab === i ? theme.primary : theme.muted,
                  borderBottom: activeTab === i ? `2px solid ${theme.primary}` : "2px solid transparent",
                  marginBottom: "-1px",
                }}
              >
                {p.title}
              </button>
            ))}
          </div>
        )}

        {/* Tab panel */}
        {current ? (
          <div
            id={`policy-panel-${current.slug}`}
            role="tabpanel"
            aria-labelledby={`tab-${current.slug}`}
            className="legal-content max-w-none"
            style={{
              fontFamily: theme.fontBody,
              fontSize: "14px",
              fontWeight: 300,
              color: theme.fg,
              lineHeight: 1.8,
              "--policy-link": theme.primary,
            } as React.CSSProperties}
          >
            <div
              dangerouslySetInnerHTML={{ __html: simpleMarkdownToHtml(current.content) }}
              className="[&>p]:mb-4 [&>h2]:mt-10 [&>h2]:mb-4 [&>h2]:text-[1rem] [&>h2]:font-normal [&>h2]:tracking-[0.02em] [&>h3]:mt-6 [&>h3]:mb-2 [&>h3]:text-[14px] [&>h3]:font-normal [&>strong]:font-medium [&_a]:text-[var(--policy-link)] [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:opacity-80"
            />
          </div>
        ) : (
          <p style={{ fontFamily: theme.fontBody, fontSize: "15px", color: theme.muted }}>
            No policy content available.
          </p>
        )}
      </div>
    </div>
  );
}
